import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { RiKey2Line, RiDownloadLine, RiFileCopyLine, RiAlertLine } from '@remixicon/react';
import {
  Button,
  CopyButton,
  CopyableValue,
  ExternalLink,
  Flex,
  InfraCodeBlock,
  InfraField,
  InfraSection,
  InfraStack,
  InfraTag,
  InfraTagRow,
  Text,
  infraCardStyles as styles,
} from '../ui/InfraPrimitives';

export const VaultInfoCard = () => {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations || {};

  const templateType = annotations['infrastructure.backstage.io/type'] || '';
  const environmentName = annotations['infrastructure.backstage.io/environment-name'];
  const publicIp = annotations['vault.infrastructure/public-ip'];
  const instanceId = annotations['vault.infrastructure/instance-id'];
  const region = annotations['vault.infrastructure/region'];

  const vaultVersion = annotations['vault.infrastructure/vault-version'];
  const vaultUrl = annotations['vault.infrastructure/vault-url'];
  const vaultUiUrl = annotations['vault.infrastructure/vault-ui-url'];
  const storageBackend = annotations['vault.infrastructure/storage-backend'];
  const autoUnseal = annotations['vault.infrastructure/auto-unseal'];
  const clusterSize = annotations['vault.infrastructure/cluster-size'];

  const sshPrivateKeyEncoded = annotations['vault.infrastructure/ssh-private-key'];
  const sshCommand = annotations['vault.infrastructure/ssh-command'];

  const hasVaultInfo = templateType === 'aws-vault-server' && (vaultUrl || publicIp);
  if (!hasVaultInfo) return null;

  const sshPrivateKey = sshPrivateKeyEncoded ? atob(sshPrivateKeyEncoded) : '';

  const downloadSSHKey = () => {
    if (!sshPrivateKeyEncoded) return;
    try {
      const decodedKey = atob(sshPrivateKeyEncoded);
      const blob = new Blob([decodedKey], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vault-${environmentName}.pem`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download SSH key:', error);
    }
  };

  return (
    <InfoCard title="🔐 HashiCorp Vault Information" icon={<RiKey2Line size={20} />}>
      <InfraStack>
        <InfraSection title="🏛️ Vault Server Configuration">
          {vaultVersion && (
            <InfraField
              label="Vault Version:"
              value={
                <InfraTagRow>
                  <span>{vaultVersion}</span>
                  <InfraTag>HashiCorp</InfraTag>
                </InfraTagRow>
              }
              mono={false}
            />
          )}
          {storageBackend && <InfraField label="Storage Backend:" value={storageBackend} />}
          {clusterSize && (
            <InfraField
              label="Cluster Size:"
              value={
                <InfraTagRow>
                  <span>{clusterSize} node(s)</span>
                  {clusterSize === '1' && <InfraTag>Single Node</InfraTag>}
                  {parseInt(clusterSize) > 1 && <InfraTag>HA Cluster</InfraTag>}
                </InfraTagRow>
              }
              mono={false}
            />
          )}
          {autoUnseal && (
            <InfraField
              label="Auto-unseal:"
              value={
                <InfraTagRow>
                  <span>{autoUnseal === 'true' ? 'Enabled (AWS KMS)' : 'Disabled'}</span>
                  {autoUnseal === 'true' && <InfraTag>AWS KMS</InfraTag>}
                </InfraTagRow>
              }
              mono={false}
            />
          )}
          {region && <InfraField label="AWS Region:" value={region} />}
          {instanceId && <CopyableValue label="Instance ID:" value={instanceId} />}
        </InfraSection>

        <InfraSection title="🌐 Vault Access">
          {vaultUrl && (
            <InfraField
              label="Vault API:"
              value={
                <span className={styles.copyRow}>
                  <ExternalLink href={vaultUrl}>{vaultUrl}</ExternalLink>
                  <CopyButton text={vaultUrl} />
                </span>
              }
              mono={false}
            />
          )}
          {vaultUiUrl && (
            <InfraField
              label="Vault UI:"
              value={
                <span className={styles.copyRow}>
                  <ExternalLink href={vaultUiUrl}>{vaultUiUrl}</ExternalLink>
                  <CopyButton text={vaultUiUrl} />
                </span>
              }
              mono={false}
            />
          )}
          {publicIp && <CopyableValue label="Public IP:" value={publicIp} />}
        </InfraSection>

        {sshCommand && (
          <InfraSection title="🔑 SSH Access">
            <InfraField label="SSH Command:" value="" mono={false} />
            <div style={{ position: 'relative' }}>
              <InfraCodeBlock variant="light">{sshCommand}</InfraCodeBlock>
              <div style={{ position: 'absolute', top: 4, right: 4 }}>
                <CopyButton text={sshCommand} />
              </div>
            </div>
            {sshPrivateKeyEncoded && (
              <>
                <Text variant="title-small" style={{ marginTop: '1rem' }}>
                  SSH Private Key:
                </Text>
                <Flex gap="2">
                  <Button variant="primary" size="small" onPress={downloadSSHKey}>
                    <RiDownloadLine size={16} />
                    Download Key
                  </Button>
                  <Button
                    variant="secondary"
                    size="small"
                    onPress={() => navigator.clipboard.writeText(sshPrivateKey)}
                  >
                    <RiFileCopyLine size={16} />
                    Copy Key
                  </Button>
                </Flex>
              </>
            )}
          </InfraSection>
        )}

        {autoUnseal !== 'true' && (
          <div
            style={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: 6,
              padding: '1rem',
            }}
          >
            <Text variant="body-medium" style={{ display: 'flex', alignItems: 'center' }}>
              <RiAlertLine size={18} style={{ marginRight: 8, color: '#ff9800' }} />
              <strong>Important:</strong>&nbsp;Vault must be initialized and unsealed before use.
            </Text>
            <Text variant="body-medium" style={{ marginTop: 8 }}>
              Run: <code>vault operator init</code> to initialize and save the unseal keys securely.
            </Text>
          </div>
        )}

        <InfraSection title="🚀 Quick Start">
          <Text variant="body-medium">1. SSH to the server using the command above</Text>
          <Text variant="body-medium">2. Initialize Vault: <code>vault operator init</code></Text>
          <Text variant="body-medium">3. Save unseal keys and root token securely</Text>
          {autoUnseal !== 'true' && (
            <Text variant="body-medium">
              4. Unseal Vault: <code>vault operator unseal</code> (3 times with different keys)
            </Text>
          )}
          <Text variant="body-medium">
            {autoUnseal !== 'true' ? '5' : '4'}. Login: <code>vault login &lt;root-token&gt;</code>
          </Text>
          <Text variant="body-medium">
            {autoUnseal !== 'true' ? '6' : '5'}. Access Vault UI at the URL above
          </Text>
        </InfraSection>
      </InfraStack>
    </InfoCard>
  );
};
